import { useState } from "react";
import { BookOpen, ChevronDown, ChevronUp, Search, Globe, Server, ShieldCheck } from "lucide-react";
import AdminOnly from "./AdminOnly";

/**
 * Step-by-step walkthrough that teaches admins how to use the existing
 * SentryHealthCheck widget below it and then verify both the browser and
 * edge synthetic events landed in Sentry. Pure presentation — no side
 * effects, no Sentry calls of its own.
 */
function SentryVerificationGuideInner() {
  const [open, setOpen] = useState(true);

  const steps: { icon: JSX.Element; title: string; body: React.ReactNode }[] = [
    {
      icon: <ShieldCheck className="h-4 w-4" />,
      title: "1. Run the Sentry Health Check",
      body: (
        <>
          Scroll to the <span className="font-semibold">Sentry Health Check</span> card
          directly below this guide and click <span className="font-semibold">Test Sentry</span>.
          The widget will (a) confirm the browser SDK initialized, (b) capture and flush a
          synthetic frontend error, and (c) invoke the <code className="font-mono">sentry-test</code>{" "}
          edge function so a server-side error is captured too.
        </>
      ),
    },
    {
      icon: <Search className="h-4 w-4" />,
      title: "2. Copy the unique tags",
      body: (
        <>
          When the run finishes, the result panel shows two correlation tags — one for the
          browser event (e.g. <code className="font-mono">fe-test-…</code>) and one for the
          edge event (e.g. <code className="font-mono">edge-test-…</code>). Both events also
          carry <code className="font-mono">synthetic=true</code> so they're easy to filter.
        </>
      ),
    },
    {
      icon: <Globe className="h-4 w-4" />,
      title: "3. Verify the browser event in Sentry",
      body: (
        <>
          Open Sentry → <span className="font-semibold">Issues</span>, switch to the project for
          this environment, and search:
          <pre className="mt-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs font-mono overflow-x-auto">tag:fe-test-&lt;your-tag&gt;</pre>
          You should see a single issue titled{" "}
          <span className="italic">"[SentryHealthCheck] synthetic frontend error"</span> with the
          tags <code className="font-mono">synthetic:true</code> and{" "}
          <code className="font-mono">source:admin-health-check</code>.
        </>
      ),
    },
    {
      icon: <Server className="h-4 w-4" />,
      title: "4. Verify the edge function event",
      body: (
        <>
          Search again with the edge tag:
          <pre className="mt-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs font-mono overflow-x-auto">tag:edge-test-&lt;your-tag&gt;</pre>
          The matching issue is titled{" "}
          <span className="italic">"[SentryHealthCheck] synthetic edge-function error"</span> and
          comes from the <code className="font-mono">sentry-test</code> function. Its presence
          confirms server-side reporting and source map upload are wired up for this release.
        </>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start justify-between gap-4 text-left"
      >
        <div>
          <h3 className="text-base font-bold text-[#1F3864] mb-1 flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> How to verify Sentry is reporting
          </h3>
          <p className="text-sm text-gray-600">
            A 4-step walkthrough for confirming that both browser and edge errors are landing in
            Sentry. Run this after every release or environment change.
          </p>
        </div>
        {open ? (
          <ChevronUp className="h-5 w-5 text-gray-400 shrink-0 mt-1" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 shrink-0 mt-1" />
        )}
      </button>

      {open && (
        <ol className="mt-4 space-y-3 border-t border-gray-100 pt-4">
          {steps.map(step => (
            <li key={step.title} className="flex items-start gap-3">
              <div className="mt-0.5 h-6 w-6 rounded-full bg-[#1F3864]/10 text-[#1F3864] flex items-center justify-center shrink-0">
                {step.icon}
              </div>
              <div className="text-sm">
                <div className="font-semibold text-gray-800">{step.title}</div>
                <div className="text-gray-600 leading-relaxed">{step.body}</div>
              </div>
            </li>
          ))}
          <li className="text-xs text-gray-500 pl-9">
            Tip: synthetic events are safe to dismiss in Sentry once you've confirmed them — they
            won't affect alert thresholds because they're tagged{" "}
            <code className="font-mono">synthetic=true</code>.
          </li>
        </ol>
      )}
    </div>
  );
}

export default function SentryVerificationGuide() {
  return (
    <AdminOnly feature="Sentry verification walkthrough">
      <SentryVerificationGuideInner />
    </AdminOnly>
  );
}